import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailRailCurve } from "../../../../../http/hrail/types/railCurves";
import { useHrailRailCurvesApi } from "../../../../../http/hrail/useRailCurves";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateRailCurveModalDialogButton from "./CreateRailCurveModalDialogButton";
import RailCurveForm from "./RailCurveForm";

type Props = {
  active?: boolean;
};

const RailCurves: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "railCurves" });
  const aborter = useRef(new AbortController());
  const railCurveApi = useHrailRailCurvesApi();
  const [itemToEdit, setItemToEdit] = useState<HrailRailCurve>();
  const [itemToDelete, setItemToDelete] = useState<HrailRailCurve>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "railCurves");
  const isActiveTab = active ?? true;
  const updateVisibility = async (railCurve: HrailRailCurve, visibility: VisibilityScopeValue) => {
    try {
      await railCurveApi.updateAdminHrailRailCurveVisibility(railCurve.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (railCurve: HrailRailCurve, newOrder: number) => {
    try {
      await railCurveApi.updateAdminHrailRailCurveOrder(railCurve.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.rail_curve");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = railCurveApi.useAdminHrailRailCurves(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.railCurves.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.railCurves.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateRailCurveModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailRailCurve>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="right">{i18n.translation.common.nominalAngle}</th>
            <th align="right">{i18n.translation.common.deflection}</th>
            <th align="left">{i18n.translation.common.plane}</th>
            <th align="right">{i18n.translation.common.horizontalDeduction}</th>
            <th align="right">{i18n.translation.common.internalDeduction}</th>
            <th align="right">{i18n.translation.common.externalDeduction}</th>
            <th align="right">{i18n.translation.common.junctions}</th>
            <th align="right">{i18n.translation.common.supports}</th>
            {/* <th align="left">{i18n.translation.systems.plural}</th> */}
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(railCurve) => (
          <>
            <Table.Td noWrap={true}>{railCurve.item?.code ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.nominalAngleDegrees, "")}
            </Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.deflectionDegrees, "")}
            </Table.Td>
            <Table.Td noWrap={false}>{formatPlane(i18n.translation, railCurve.plane)}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.horizontalDeductionMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.internalDeductionMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.externalDeductionMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.junctionsRequired, "")}
            </Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(railCurve.supportsRequired, "")}
            </Table.Td>
            {/* <td>{railCurve.systems?.map((system) => system.name).join("; ")}</td> */}
            <td align="center">
              <Visibility value={railCurve.visibility} onChange={(value) => updateVisibility(railCurve, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={railCurve.order} onUpdate={(newOrder) => updateOrder(railCurve, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(railCurve) => setItemToEdit(railCurve)}
        rowActions={(railCurve) => [
          getEditAction(i18n, () => setItemToEdit(railCurve)),
          accessesAction(railCurve.item),
          getDeleteAction(i18n, () => setItemToDelete(railCurve), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.railCurves.edit}>
          <RailCurveForm railCurve={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.railCurves.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                railCurveApi
                  .deleteAdminHrailRailCurve(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.railCurves.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

const formatPlane = (translation: ReturnType<typeof useI18n>["translation"], value?: string | null) => {
  switch (value) {
    case "horizontal":
      return translation.common.horizontal;
    case "vertical":
      return translation.common.vertical;
    default:
      return value ?? "";
  }
};

export default RailCurves;
